import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
    Play,
    Code,
    Eye,
    Download,
    Copy,
    RotateCcw,
    Maximize,
    Minimize,
    Settings,
    FileText,
    Zap,
    Search,
    Replace,
    Sun,
    Moon,
    Palette,
    FolderOpen,
    Save,
    Upload,
    Code2,
    Monitor,
    Smartphone,
    Tablet,
    Layers,
    GitBranch,
    History,
    BookOpen,
    Cpu,
    Globe,
    CheckCircle,
    AlertCircle,
    Info,
    ChevronDown,
    ChevronRight,
    Maximize2,
    Minimize2,
    HelpCircle,
    Star,
    Sparkles,
    Shield,
    Paintbrush,
    Layout,
    Database,
    FileCode,
    Terminal
} from 'lucide-react';
import HTMLViewerFeatures from './Information/HtmlViewer';

interface ViewerSettings {
    theme: 'dark' | 'light';
    fontSize: number;
    tabSize: number;
    wordWrap: boolean;
    lineNumbers: boolean;
    minimap: boolean;
    autoFormat: boolean;
    autoSave: boolean;
    livePreview: boolean;
    showInvisibles: boolean;
    bracketMatching: boolean;
    codeCompletion: boolean;
}

interface CodeValidation {
    isValid: boolean;
    errors: Array<{
        line: number;
        message: string;
        type: 'error' | 'warning' | 'info';
    }>;
}

const AdvancedHTMLViewer: React.FC = () => {
    const [htmlCode, setHtmlCode] = useState(`<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Advanced HTML Viewer Demo</title>
    <style>
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        
        body {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            min-height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 20px;
        }
        
        .container {
            max-width: 800px;
            width: 100%;
            background: rgba(255, 255, 255, 0.95);
            backdrop-filter: blur(20px);
            border-radius: 20px;
            padding: 40px;
            box-shadow: 0 25px 45px rgba(0,0,0,0.1);
            border: 1px solid rgba(255,255,255,0.18);
        }
        
        h1 {
            color: #333;
            text-align: center;
            margin-bottom: 30px;
            font-size: 2.5rem;
            background: linear-gradient(45deg, #667eea, #764ba2);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
        }
        
        .feature-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
            gap: 25px;
            margin: 30px 0;
        }
        
        .feature-card {
            background: linear-gradient(145deg, #f8f9fa, #e9ecef);
            padding: 25px;
            border-radius: 15px;
            border-left: 5px solid #007bff;
            transition: all 0.3s ease;
            position: relative;
            overflow: hidden;
        }
        
        .feature-card::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            height: 3px;
            background: linear-gradient(90deg, #007bff, #0056b3);
        }
        
        .feature-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 15px 30px rgba(0,123,255,0.2);
        }
        
        .feature-card h3 {
            color: #333;
            margin-bottom: 15px;
            font-size: 1.2rem;
        }
        
        .feature-card ul {
            list-style: none;
            padding: 0;
        }
        
        .feature-card li {
            color: #666;
            margin: 8px 0;
            padding-left: 20px;
            position: relative;
        }
        
        .feature-card li::before {
            content: '✓';
            position: absolute;
            left: 0;
            color: #28a745;
            font-weight: bold;
        }
        
        .btn {
            background: linear-gradient(45deg, #007bff, #0056b3);
            color: white;
            padding: 15px 30px;
            border: none;
            border-radius: 25px;
            cursor: pointer;
            font-size: 1rem;
            font-weight: 600;
            transition: all 0.3s ease;
            text-transform: uppercase;
            letter-spacing: 1px;
            margin: 10px;
            box-shadow: 0 8px 20px rgba(0,123,255,0.3);
        }
        
        .btn:hover {
            transform: translateY(-3px);
            box-shadow: 0 12px 25px rgba(0,123,255,0.4);
        }
        
        .btn:active {
            transform: translateY(-1px);
        }
        
        .demo-section {
            margin: 30px 0;
            padding: 25px;
            background: rgba(255,255,255,0.7);
            border-radius: 15px;
            border: 1px solid rgba(0,123,255,0.2);
        }
        
        .code-preview {
            background: #2d3748;
            color: #cbd5e0;
            padding: 20px;
            border-radius: 10px;
            font-family: 'Courier New', monospace;
            font-size: 0.9rem;
            overflow-x: auto;
            margin: 15px 0;
        }
        
        @media (max-width: 768px) {
            .container {
                padding: 25px;
                margin: 10px;
            }
            
            h1 {
                font-size: 2rem;
            }
            
            .feature-grid {
                grid-template-columns: 1fr;
            }
        }
        
        .loading-animation {
            width: 50px;
            height: 50px;
            margin: 20px auto;
            border: 3px solid #f3f3f3;
            border-top: 3px solid #007bff;
            border-radius: 50%;
            animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
        }
    </style>
</head>
<body>
    <div class="container">
        <h1>🚀 Advanced HTML Viewer</h1>
        
        <div class="feature-grid">
            <div class="feature-card">
                <h3>🎨 Editor Features</h3>
                <ul>
                    <li>Syntax highlighting</li>
                    <li>Auto-completion</li>
                    <li>Code formatting</li>
                    <li>Error detection</li>
                    <li>Live preview</li>
                </ul>
            </div>
            
            <div class="feature-card">
                <h3>📱 Responsive Testing</h3>
                <ul>
                    <li>Mobile preview</li>
                    <li>Tablet preview</li>
                    <li>Desktop preview</li>
                    <li>Custom dimensions</li>
                    <li>Device rotation</li>
                </ul>
            </div>
            
            <div class="feature-card">
                <h3>⚡ Performance</h3>
                <ul>
                    <li>Fast rendering</li>
                    <li>Auto-save</li>
                    <li>Version history</li>
                    <li>Export options</li>
                    <li>Code optimization</li>
                </ul>
            </div>
        </div>
        
        <div class="demo-section">
            <h3>Interactive Demo</h3>
            <p>This HTML viewer includes advanced features like real-time validation, multiple export formats, and responsive design testing.</p>
            
            <div class="code-preview">
&lt;div class="example"&gt;
    &lt;h2&gt;Sample Code&lt;/h2&gt;
    &lt;p&gt;Live preview with syntax highlighting&lt;/p&gt;
&lt;/div&gt;
            </div>
            
            <button class="btn" onclick="alert('🎉 Interactive features work perfectly!')">
                Test Interactivity
            </button>
            
            <button class="btn" onclick="document.body.style.background = 'linear-gradient(45deg, #ff6b6b, #feca57)'">
                Change Theme
            </button>
        </div>
        
        <div class="loading-animation"></div>
        
        <div style="text-align: center; margin-top: 30px;">
            <p style="color: #666; font-style: italic;">
                ✨ Built with modern web technologies for the best experience
            </p>
        </div>
    </div>
</body>
</html>`);

    const [activeTab, setActiveTab] = useState<'code' | 'preview' | 'split'>('split');
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [replaceQuery, setReplaceQuery] = useState('');
    const [showExportOptions, setShowExportOptions] = useState(false);
    const [showDevicePreview, setShowDevicePreview] = useState(false);
    const [selectedDevice, setSelectedDevice] = useState<'desktop' | 'tablet' | 'mobile'>('desktop');
    const [autoRun, setAutoRun] = useState(true);
    const [showSearch, setShowSearch] = useState(false);
    const [showValidation, setShowValidation] = useState(true);
    const [showFileManager, setShowFileManager] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [showFeatureInfo, setShowFeatureInfo] = useState(false);
    const [validation, setValidation] = useState<CodeValidation>({ isValid: true, errors: [] });
const safeConfirm = (message: string) => window.confirm(message);
    const [settings, setSettings] = useState<ViewerSettings>({
        theme: 'dark',
        fontSize: 14,
        tabSize: 2,
        wordWrap: true,
        lineNumbers: true,
        minimap: false,
        autoFormat: true,
        autoSave: true,
        livePreview: true,
        showInvisibles: false,
        bracketMatching: true,
        codeCompletion: true
    });

    const iframeRef = useRef<HTMLIFrameElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Device dimensions
    const deviceDimensions = {
        desktop: { width: '100%', height: '100%' },
        tablet: { width: '768px', height: '1024px' },
        mobile: { width: '375px', height: '667px' }
    };

    // Feature list for information section
    const features = [
        {
            category: "Editor Features",
            icon: <Code className="w-4 h-4" />,
            items: [
                "Advanced syntax highlighting with color coding",
                "Real-time HTML validation and error detection",
                "Auto-formatting and code beautification",
                "Line numbers and bracket matching",
                "Word wrap and invisible character display",
                "Customizable font size and tab spacing",
                "Search and replace functionality",
                "Code folding and collapsing"
            ]
        },
        {
            category: "Live Preview",
            icon: <Eye className="w-4 h-4" />,
            items: [
                "Real-time preview with auto-refresh",
                "Responsive design testing modes",
                "Device-specific preview (Desktop, Tablet, Mobile)",
                "Safe sandboxed iframe execution",
                "Manual and automatic preview updates",
                "Full-screen preview mode"
            ]
        },
        {
            category: "File Management",
            icon: <FileCode className="w-4 h-4" />,
            items: [
                "Import HTML files from local storage",
                "Export as HTML, TXT, or JSON formats",
                "Auto-save functionality with local storage",
                "Quick access to HTML templates",
                "Copy code to clipboard",
                "Drag and drop file support"
            ]
        },
        {
            category: "User Interface",
            icon: <Layout className="w-4 h-4" />,
            items: [
                "Dark and light theme support",
                "Split view (Code + Preview)",
                "Fullscreen editing mode",
                "Responsive interface design",
                "Customizable workspace layout",
                "Keyboard shortcuts support"
            ]
        },
        {
            category: "Development Tools",
            icon: <Terminal className="w-4 h-4" />,
            items: [
                "HTML validation with detailed error reporting",
                "Performance optimization suggestions",
                "Code quality analysis",
                "Character, word, and line counting",
                "Browser compatibility checking",
                "Accessibility recommendations"
            ]
        },
        {
            category: "Advanced Features",
            icon: <Sparkles className="w-4 h-4" />,
            items: [
                "Multi-device responsive testing",
                "Version history and undo/redo",
                "Code completion and snippets",
                "Live collaboration ready",
                "Plugin architecture support",
                "Custom theme creation"
            ]
        }
    ];

    // Enhanced syntax highlighting
    const highlightHTML = useCallback((code: string): string => {
        return code
            .replace(/(&lt;!--[\s\S]*?--&gt;)/g, '<span class="comment">$1</span>')
            .replace(/(&lt;!DOCTYPE[^&]*&gt;)/gi, '<span class="doctype">$1</span>')
            .replace(/(&lt;\/?)([^&\s/>]+)/g, '$1<span class="tag">$2</span>')
            .replace(/([\w-]+)(\s*=)/g, '<span class="attr">$1</span>$2')
            .replace(/=(&quot;[^&]*&quot;|&#039;[^&]*&#039;)/g, '=<span class="string">$1</span>')
            .replace(/&lt;/g, '<span class="bracket">&lt;</span>')
            .replace(/&gt;/g, '<span class="bracket">&gt;</span>')
            .replace(/(&amp;[a-zA-Z0-9#]+;)/g, '<span class="entity">$1</span>');
    }, []);

    const escapeHtml = useCallback((unsafe: string): string => {
        return unsafe
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#039;");
    }, []);

    // Enhanced code validation
    const validateHTML = useCallback((code: string): CodeValidation => {
        const errors: CodeValidation['errors'] = [];
        const lines = code.split('\n');

        lines.forEach((line, index) => {
            const lineNum = index + 1;

            // Check for unclosed tags
            const openTags = line.match(/<([a-zA-Z][a-zA-Z0-9]*)[^>]*>/g);
            const closeTags = line.match(/<\/([a-zA-Z][a-zA-Z0-9]*)[^>]*>/g);

            if (openTags && closeTags) {
                openTags.forEach(tag => {
                    const tagName = tag.match(/<([a-zA-Z][a-zA-Z0-9]*)/)?.[1];
                    if (tagName && !['img', 'br', 'hr', 'input', 'meta', 'link'].includes(tagName.toLowerCase())) {
                        const closeTag = `</${tagName}>`;
                        if (!line.includes(closeTag) && !closeTags.some(ct => ct.includes(tagName))) {
                            errors.push({
                                line: lineNum,
                                message: `Unclosed tag: ${tagName}`,
                                type: 'warning'
                            });
                        }
                    }
                });
            }

            // Check for missing alt attributes on images
            if (line.includes('<img') && !line.includes('alt=')) {
                errors.push({
                    line: lineNum,
                    message: 'Image missing alt attribute',
                    type: 'warning'
                });
            }

            // Check for inline styles (best practice warning)
            if (line.includes('style=')) {
                errors.push({
                    line: lineNum,
                    message: 'Consider using external CSS instead of inline styles',
                    type: 'info'
                });
            }
        });

        return {
            isValid: errors.filter(e => e.type === 'error').length === 0,
            errors
        };
    }, []);

    // Enhanced formatting function
    const formatHTML = useCallback((html: string): string => {
        let formatted = html;
        let indent = 0;
        const tab = ' '.repeat(settings.tabSize);

        // Clean up the HTML first
        formatted = formatted.replace(/>\s*</g, '><');
        formatted = formatted.replace(/></g, '>\n<');

        const lines = formatted.split('\n');
        const formattedLines: string[] = [];

        lines.forEach(line => {
            const trimmed = line.trim();
            if (!trimmed) return;

            // Decrease indent for closing tags
            if (trimmed.startsWith('</')) {
                indent = Math.max(0, indent - 1);
            }

            // Add the line with proper indentation
            formattedLines.push(tab.repeat(indent) + trimmed);

            // Increase indent for opening tags (but not self-closing or closing tags)
            if (trimmed.startsWith('<') && !trimmed.startsWith('</') && !trimmed.endsWith('/>')) {
                const tagMatch = trimmed.match(/<([a-zA-Z][a-zA-Z0-9]*)/);
                const tagName = tagMatch?.[1]?.toLowerCase();
                const selfClosing = ['img', 'br', 'hr', 'input', 'meta', 'link', 'area', 'base', 'col', 'embed', 'source', 'track', 'wbr'];

                if (tagName && !selfClosing.includes(tagName)) {
                    // Check if tag is closed on the same line
                    if (!trimmed.includes(`</${tagName}>`)) {
                        indent++;
                    }
                }
            }
        });

        return formattedLines.join('\n');
    }, [settings.tabSize]);

    // File operations
    const handleFileUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file && file.type === 'text/html') {
            const reader = new FileReader();
            reader.onload = (e) => {
                const content = e.target?.result as string;
                setHtmlCode(content);
            };
            reader.readAsText(file);
        }
    }, []);

    const handleFormat = useCallback(() => {
        const formatted = formatHTML(htmlCode);
        setHtmlCode(formatted);
    }, [htmlCode, formatHTML]);

    const handleSearch = useCallback(() => {
        if (!searchQuery || !textareaRef.current) return;

        const textarea = textareaRef.current;
        const text = textarea.value;
        const index = text.toLowerCase().indexOf(searchQuery.toLowerCase());

        if (index !== -1) {
            textarea.focus();
            textarea.setSelectionRange(index, index + searchQuery.length);
            textarea.scrollTop = textarea.scrollHeight * (index / text.length);
        }
    }, [searchQuery]);

    const handleReplace = useCallback(() => {
        if (!searchQuery) return;
        const newCode = htmlCode.replace(new RegExp(searchQuery, 'gi'), replaceQuery);
        setHtmlCode(newCode);
    }, [htmlCode, searchQuery, replaceQuery]);

    const copyToClipboard = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(htmlCode);
            // Could add a toast notification here
        } catch (err) {
            console.error('Failed to copy: ', err);
        }
    }, [htmlCode]);

    const downloadHTML = useCallback(() => {
        const blob = new Blob([htmlCode], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'document.html';
        a.click();
        URL.revokeObjectURL(url);
    }, [htmlCode]);

    const exportAs = useCallback((format: string) => {
        switch (format) {
            case 'html':
                downloadHTML();
                break;
            case 'txt':
                const txtBlob = new Blob([htmlCode], { type: 'text/plain' });
                const txtUrl = URL.createObjectURL(txtBlob);
                const txtLink = document.createElement('a');
                txtLink.href = txtUrl;
                txtLink.download = 'code.txt';
                txtLink.click();
                URL.revokeObjectURL(txtUrl);
                break;
            case 'json':
                const jsonData = {
                    content: htmlCode,
                    metadata: {
                        lines: htmlCode.split('\n').length,
                        characters: htmlCode.length,
                        created: new Date().toISOString()
                    }
                };
                const jsonBlob = new Blob([JSON.stringify(jsonData, null, 2)], { type: 'application/json' });
                const jsonUrl = URL.createObjectURL(jsonBlob);
                const jsonLink = document.createElement('a');
                jsonLink.href = jsonUrl;
                jsonLink.download = 'document.json';
                jsonLink.click();
                URL.revokeObjectURL(jsonUrl);
                break;
        }
        setShowExportOptions(false);
    }, [htmlCode, downloadHTML]);

    const runCode = useCallback(() => {
        if (iframeRef.current) {
            const iframe = iframeRef.current;
            const doc = iframe.contentDocument || iframe.contentWindow?.document;
            if (doc) {
                doc.open();
                doc.write(htmlCode);
                doc.close();
            }
        }
    }, [htmlCode]);

    const loadTemplate = useCallback((template: string) => {
        const templates = {
            basic: `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Basic Template</title>
</head>
<body>
    <header>
        <h1>Welcome</h1>
    </header>
    <main>
        <p>Your content here</p>
    </main>
    <footer>
        <p>&copy; 2025</p>
    </footer>
</body>
</html>`,
            responsive: `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Responsive Layout</title>
    <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
        .container { max-width: 1200px; margin: 0 auto; padding: 20px; }
        .grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 20px; }
        .card { 
            background: white; 
            padding: 20px; 
            border-radius: 8px; 
            box-shadow: 0 2px 10px rgba(0,0,0,0.1);
            transition: transform 0.2s;
        }
        .card:hover { transform: translateY(-2px); }
        @media (max-width: 768px) { 
            .container { padding: 10px; }
            .grid { grid-template-columns: 1fr; }
        }
    </style>
</head>
<body>
    <div class="container">
        <h1>Responsive Grid Layout</h1>
        <div class="grid">
            <div class="card">
                <h3>Feature 1</h3>
                <p>Responsive design that works on all devices.</p>
            </div>
            <div class="card">
                <h3>Feature 2</h3>
                <p>Modern CSS Grid layout system.</p>
            </div>
            <div class="card">
                <h3>Feature 3</h3>
                <p>Clean and professional appearance.</p>
            </div>
        </div>
    </div>
</body>
</html>`,
            modern: `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Modern Design</title>
    <style>
        :root {
            --primary: #667eea;
            --secondary: #764ba2;
            --accent: #f093fb;
            --text: #333;
            --bg: #f8fafc;
        }
        
        * { margin: 0; padding: 0; box-sizing: border-box; }
        
        body {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            background: linear-gradient(135deg, var(--primary), var(--secondary));
            min-height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 20px;
        }
        
        .hero {
            text-align: center;
            background: rgba(255, 255, 255, 0.95);
            backdrop-filter: blur(20px);
            border-radius: 20px;
            padding: 60px 40px;
            box-shadow: 0 25px 50px rgba(0,0,0,0.1);
            border: 1px solid rgba(255,255,255,0.2);
            max-width: 600px;
        }
        
        .hero h1 {
            font-size: 3rem;
            background: linear-gradient(45deg, var(--primary), var(--accent));
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            margin-bottom: 20px;
        }
        
        .hero p {
            font-size: 1.2rem;
            color: #666;
            margin-bottom: 30px;
            line-height: 1.6;
        }
        
        .btn {
            background: linear-gradient(45deg, var(--primary), var(--secondary));
            color: white;
            padding: 15px 30px;
            border: none;
            border-radius: 25px;
            font-size: 1rem;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.3s ease;
            text-transform: uppercase;
            letter-spacing: 1px;
            box-shadow: 0 10px 25px rgba(102, 126, 234, 0.3);
        }
        
        .btn:hover {
            transform: translateY(-3px);
            box-shadow: 0 15px 35px rgba(102, 126, 234, 0.4);
        }
        
        @media (max-width: 768px) {
            .hero { padding: 40px 20px; }
            .hero h1 { font-size: 2.5rem; }
        }
    </style>
</head>
<body>
    <div class="hero">
        <h1>Modern UI</h1>
        <p>Beautiful glassmorphism design with smooth animations and modern aesthetics.</p>
        <button class="btn" onclick="alert('🚀 Welcome to the future of web design!')">
            Get Started
        </button>
    </div>
</body>
</html>`
        };
        setHtmlCode(templates[template as keyof typeof templates] || templates.basic);
    }, []);

    // Update preview
    useEffect(() => {
        if (autoRun && settings.livePreview) {
            const debounceTimer = setTimeout(() => {
                runCode();
            }, 300);

            return () => clearTimeout(debounceTimer);
        }
    }, [htmlCode, autoRun, settings.livePreview, runCode]);

    // Validate code
    useEffect(() => {
        if (showValidation) {
            const validationResult = validateHTML(htmlCode);
            setValidation(validationResult);
        }
    }, [htmlCode, showValidation, validateHTML]);

    // Auto-save
    useEffect(() => {
        if (settings.autoSave) {
            const timer = setTimeout(() => {
                localStorage.setItem('htmlviewer_autosave', htmlCode);
            }, 2000);

            return () => clearTimeout(timer);
        }
    }, [htmlCode, settings.autoSave]);

    // Load auto-saved content on mount
    useEffect(() => {
        const saved = localStorage.getItem('htmlviewer_autosave');
        if (saved && saved !== htmlCode) {
            // Could show a restore dialog here
        }
    }, []);

    const highlightedCode = useMemo(() => {
        return highlightHTML(escapeHtml(htmlCode));
    }, [htmlCode, highlightHTML, escapeHtml]);

    const lineCount = htmlCode.split('\n').length;
    const charCount = htmlCode.length;
    const wordCount = htmlCode.split(/\s+/).filter(word => word.length > 0).length;

    return (
        <>
            <div className={`${isFullscreen ? 'fixed inset-0 z-50' : 'h-screen'} flex flex-col ${settings.theme === 'dark' ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'} transition-all duration-300`}>
                {/* Header */}
                <div className={`flex items-center justify-between p-3 border-b ${settings.theme === 'dark' ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'} shadow-sm`}>
                    <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-2">
                            <Code2 className="w-6 h-6 text-blue-500" />
                            <h1 className="text-lg font-bold bg-gradient-to-r from-blue-500 to-purple-600 bg-clip-text text-transparent">
                                Advanced HTML Viewer
                            </h1>
                        </div>

                        {/* Tab Selector */}
                        <div className={`flex space-x-1 rounded-lg p-1 ${settings.theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'}`}>
                            <button
                                onClick={() => setActiveTab('code')}
                                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 flex items-center space-x-1 ${activeTab === 'code'
                                        ? `${settings.theme === 'dark' ? 'bg-gray-600 text-white shadow-lg' : 'bg-white text-gray-900 shadow-md'} transform scale-105`
                                        : `${settings.theme === 'dark' ? 'text-gray-300 hover:bg-gray-600 hover:text-white' : 'text-gray-600 hover:bg-gray-200 hover:text-gray-900'}`
                                    }`}
                            >
                                <FileText className="w-4 h-4" />
                                <span>Code</span>
                            </button>
                            <button
                                onClick={() => setActiveTab('preview')}
                                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 flex items-center space-x-1 ${activeTab === 'preview'
                                        ? `${settings.theme === 'dark' ? 'bg-gray-600 text-white shadow-lg' : 'bg-white text-gray-900 shadow-md'} transform scale-105`
                                        : `${settings.theme === 'dark' ? 'text-gray-300 hover:bg-gray-600 hover:text-white' : 'text-gray-600 hover:bg-gray-200 hover:text-gray-900'}`
                                    }`}
                            >
                                <Eye className="w-4 h-4" />
                                <span>Preview</span>
                            </button>
                            <button
                                onClick={() => setActiveTab('split')}
                                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 flex items-center space-x-1 ${activeTab === 'split'
                                        ? `${settings.theme === 'dark' ? 'bg-gray-600 text-white shadow-lg' : 'bg-white text-gray-900 shadow-md'} transform scale-105`
                                        : `${settings.theme === 'dark' ? 'text-gray-300 hover:bg-gray-600 hover:text-white' : 'text-gray-600 hover:bg-gray-200 hover:text-gray-900'}`
                                    }`}
                            >
                                <Layers className="w-4 h-4" />
                                <span>Split</span>
                            </button>
                        </div>
                    </div>

                    {/* Toolbar */}
                    <div className="flex items-center space-x-2">
                        <button
                            onClick={runCode}
                            className="flex items-center space-x-1 px-3 py-1.5 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm font-medium"
                            title="Run Code (Ctrl+Enter)"
                        >
                            <Play className="w-4 h-4" />
                            <span>Run</span>
                        </button>

                        <button
                            onClick={() => setAutoRun(!autoRun)}
                            className={`p-2 rounded-lg transition-colors ${autoRun
                                    ? 'bg-blue-500 text-white hover:bg-blue-600'
                                    : 'bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600'
                                }`}
                            title="Toggle Auto-Run"
                        >
                            <Zap className="w-4 h-4" />
                        </button>

                        <button
                            onClick={() => setShowDevicePreview(!showDevicePreview)}
                            className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                            title="Device Preview"
                        >
                            <Monitor className="w-4 h-4" />
                        </button>

                        <button
                            onClick={() => setShowSearch(!showSearch)}
                            className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                            title="Search & Replace"
                        >
                            <Search className="w-4 h-4" />
                        </button>

                        <button
                            onClick={handleFormat}
                            className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                            title="Format Code"
                        >
                            <Palette className="w-4 h-4" />
                        </button>

                        <button
                            onClick={() => setShowValidation(!showValidation)}
                            className={`p-2 rounded-lg transition-colors ${showValidation
                                    ? 'bg-blue-500 text-white hover:bg-blue-600'
                                    : 'hover:bg-gray-200 dark:hover:bg-gray-700'
                                }`}
                            title="Toggle Validation"
                        >
                            <CheckCircle className="w-4 h-4" />
                        </button>

                        <button
                            onClick={copyToClipboard}
                            className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                            title="Copy Code"
                        >
                            <Copy className="w-4 h-4" />
                        </button>

                        <button
                            onClick={() => setShowFileManager(!showFileManager)}
                            className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                            title="File Manager"
                        >
                            <FolderOpen className="w-4 h-4" />
                        </button>

                        <div className="relative">
                            <button
                                onClick={() => setShowExportOptions(!showExportOptions)}
                                className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                                title="Export Options"
                            >
                                <Download className="w-4 h-4" />
                            </button>

                            {showExportOptions && (
                                <div className={`absolute right-0 top-full mt-2 w-48 ${settings.theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border rounded-lg shadow-xl z-50`}>
                                    <div className="p-2">
                                        <div className="text-sm font-medium mb-2">Export As:</div>
                                        <button
                                            onClick={() => exportAs('html')}
                                            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 rounded flex items-center space-x-2"
                                        >
                                            <Globe className="w-4 h-4" />
                                            <span>HTML File</span>
                                        </button>
                                        <button
                                            onClick={() => exportAs('txt')}
                                            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 rounded flex items-center space-x-2"
                                        >
                                            <FileText className="w-4 h-4" />
                                            <span>Text File</span>
                                        </button>
                                        <button
                                            onClick={() => exportAs('json')}
                                            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 rounded flex items-center space-x-2"
                                        >
                                            <Code className="w-4 h-4" />
                                            <span>JSON Data</span>
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        <button
                            onClick={() => setShowSettings(!showSettings)}
                            className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                            title="Settings"
                        >
                            <Settings className="w-4 h-4" />
                        </button>

                        <button
                            onClick={() => setIsFullscreen(!isFullscreen)}
                            className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                            title="Toggle Fullscreen"
                        >
                            {isFullscreen ? <Minimize className="w-4 h-4" /> : <Maximize className="w-4 h-4" />}
                        </button>
                    </div>
                </div>

                {/* Device Preview Bar */}
                {showDevicePreview && (
                    <div className={`p-3 border-b ${settings.theme === 'dark' ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-100'}`}>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                                <span className="text-sm font-medium">Preview Mode:</span>
                                <div className="flex space-x-2">
                                    <button
                                        onClick={() => setSelectedDevice('desktop')}
                                        className={`flex items-center space-x-1 px-3 py-1 rounded ${selectedDevice === 'desktop' ? 'bg-blue-500 text-white' : 'bg-gray-200 dark:bg-gray-700'
                                            }`}
                                    >
                                        <Monitor className="w-4 h-4" />
                                        <span className="text-sm">Desktop</span>
                                    </button>
                                    <button
                                        onClick={() => setSelectedDevice('tablet')}
                                        className={`flex items-center space-x-1 px-3 py-1 rounded ${selectedDevice === 'tablet' ? 'bg-blue-500 text-white' : 'bg-gray-200 dark:bg-gray-700'
                                            }`}
                                    >
                                        <Tablet className="w-4 h-4" />
                                        <span className="text-sm">Tablet</span>
                                    </button>
                                    <button
                                        onClick={() => setSelectedDevice('mobile')}
                                        className={`flex items-center space-x-1 px-3 py-1 rounded ${selectedDevice === 'mobile' ? 'bg-blue-500 text-white' : 'bg-gray-200 dark:bg-gray-700'
                                            }`}
                                    >
                                        <Smartphone className="w-4 h-4" />
                                        <span className="text-sm">Mobile</span>
                                    </button>
                                </div>
                            </div>
                            <div className="text-sm text-gray-500">
                                {deviceDimensions[selectedDevice].width} × {deviceDimensions[selectedDevice].height}
                            </div>
                        </div>
                    </div>
                )}

                {/* Search Panel */}
                {showSearch && (
                    <div className={`p-3 border-b ${settings.theme === 'dark' ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-100'}`}>
                        <div className="flex items-center space-x-4">
                            <div className="flex items-center space-x-2">
                                <Search className="w-4 h-4" />
                                <input
                                    type="text"
                                    placeholder="Search..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className={`px-3 py-1 rounded border ${settings.theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'} text-sm`}
                                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                />
                                <button
                                    onClick={handleSearch}
                                    className="px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600 transition-colors"
                                >
                                    Find
                                </button>
                            </div>

                            <div className="flex items-center space-x-2">
                                <Replace className="w-4 h-4" />
                                <input
                                    type="text"
                                    placeholder="Replace with..."
                                    value={replaceQuery}
                                    onChange={(e) => setReplaceQuery(e.target.value)}
                                    className={`px-3 py-1 rounded border ${settings.theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'} text-sm`}
                                />
                                <button
                                    onClick={handleReplace}
                                    className="px-3 py-1 bg-green-500 text-white rounded text-sm hover:bg-green-600 transition-colors"
                                >
                                    Replace All
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* File Manager Panel */}
                {showFileManager && (
                    <div className={`p-3 border-b ${settings.theme === 'dark' ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-100'}`}>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                                <span className="text-sm font-medium">File Operations:</span>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".html,.htm"
                                    onChange={handleFileUpload}
                                    className="hidden"
                                />
                                <button
                                    onClick={() => fileInputRef.current?.click()}
                                    className="flex items-center space-x-1 px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600 transition-colors"
                                >
                                    <Upload className="w-4 h-4" />
                                    <span>Load File</span>
                                </button>
                                <button
                                    onClick={downloadHTML}
                                    className="flex items-center space-x-1 px-3 py-1 bg-green-500 text-white rounded text-sm hover:bg-green-600 transition-colors"
                                >
                                    <Save className="w-4 h-4" />
                                    <span>Save</span>
                                </button>
                            </div>
                            <div className="flex items-center space-x-2">
                                <span className="text-sm">Quick Templates:</span>
                                <button
                                    onClick={() => loadTemplate('basic')}
                                    className="px-2 py-1 text-xs bg-gray-200 dark:bg-gray-700 rounded hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                                >
                                    Basic
                                </button>
                                <button
                                    onClick={() => loadTemplate('responsive')}
                                    className="px-2 py-1 text-xs bg-gray-200 dark:bg-gray-700 rounded hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                                >
                                    Responsive
                                </button>
                                <button
                                    onClick={() => loadTemplate('modern')}
                                    className="px-2 py-1 text-xs bg-gray-200 dark:bg-gray-700 rounded hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                                >
                                    Modern
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Settings Panel */}
                {showSettings && (
                    <div className={`p-4 border-b ${settings.theme === 'dark' ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-100'}`}>
                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                            <div>
                                <label className="block text-sm font-medium mb-2">Theme</label>
                                <button
                                    onClick={() => setSettings(prev => ({ ...prev, theme: prev.theme === 'dark' ? 'light' : 'dark' }))}
                                    className="flex items-center space-x-2 px-3 py-2 rounded border hover:bg-gray-200 dark:hover:bg-gray-700 w-full"
                                >
                                    {settings.theme === 'dark' ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
                                    <span className="text-sm">{settings.theme === 'dark' ? 'Dark' : 'Light'}</span>
                                </button>
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-2">Font Size</label>
                                <input
                                    type="range"
                                    min="10"
                                    max="24"
                                    value={settings.fontSize}
                                    onChange={(e) => setSettings(prev => ({ ...prev, fontSize: parseInt(e.target.value) }))}
                                    className="w-full"
                                />
                                <span className="text-xs text-gray-500">{settings.fontSize}px</span>
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-2">Tab Size</label>
                                <input
                                    type="range"
                                    min="2"
                                    max="8"
                                    value={settings.tabSize}
                                    onChange={(e) => setSettings(prev => ({ ...prev, tabSize: parseInt(e.target.value) }))}
                                    className="w-full"
                                />
                                <span className="text-xs text-gray-500">{settings.tabSize} spaces</span>
                            </div>

                            <div className="space-y-2">
                                <label className="block text-sm font-medium mb-2">Editor Options</label>
                                <label className="flex items-center space-x-2">
                                    <input
                                        type="checkbox"
                                        checked={settings.wordWrap}
                                        onChange={(e) => setSettings(prev => ({ ...prev, wordWrap: e.target.checked }))}
                                        className="rounded"
                                    />
                                    <span className="text-sm">Word Wrap</span>
                                </label>

                                <label className="flex items-center space-x-2">
                                    <input
                                        type="checkbox"
                                        checked={settings.lineNumbers}
                                        onChange={(e) => setSettings(prev => ({ ...prev, lineNumbers: e.target.checked }))}
                                        className="rounded"
                                    />
                                    <span className="text-sm">Line Numbers</span>
                                </label>

                                <label className="flex items-center space-x-2">
                                    <input
                                        type="checkbox"
                                        checked={settings.showInvisibles}
                                        onChange={(e) => setSettings(prev => ({ ...prev, showInvisibles: e.target.checked }))}
                                        className="rounded"
                                    />
                                    <span className="text-sm">Show Invisibles</span>
                                </label>
                            </div>

                            <div className="space-y-2">
                                <label className="block text-sm font-medium mb-2">Advanced Features</label>
                                <label className="flex items-center space-x-2">
                                    <input
                                        type="checkbox"
                                        checked={settings.autoSave}
                                        onChange={(e) => setSettings(prev => ({ ...prev, autoSave: e.target.checked }))}
                                        className="rounded"
                                    />
                                    <span className="text-sm">Auto Save</span>
                                </label>

                                <label className="flex items-center space-x-2">
                                    <input
                                        type="checkbox"
                                        checked={settings.livePreview}
                                        onChange={(e) => setSettings(prev => ({ ...prev, livePreview: e.target.checked }))}
                                        className="rounded"
                                    />
                                    <span className="text-sm">Live Preview</span>
                                </label>

                                <label className="flex items-center space-x-2">
                                    <input
                                        type="checkbox"
                                        checked={settings.bracketMatching}
                                        onChange={(e) => setSettings(prev => ({ ...prev, bracketMatching: e.target.checked }))}
                                        className="rounded"
                                    />
                                    <span className="text-sm">Bracket Matching</span>
                                </label>
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-2">Actions</label>
                                <div className="space-y-1">
                                    <button
                                        onClick={() => setIsCollapsed(!isCollapsed)}
                                        className="w-full text-left px-3 py-1 text-sm hover:bg-gray-200 dark:hover:bg-gray-700 rounded flex items-center space-x-2"
                                    >
                                        {isCollapsed ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
                                        <span>{isCollapsed ? 'Expand' : 'Collapse'} Code</span>
                                    </button>
                                    <button
                                        onClick={() => {
                                            if (safeConfirm('Clear all code? This cannot be undone.')) {
                                                setHtmlCode('<!DOCTYPE html>\n<html>\n<head>\n  <title>New Document</title>\n</head>\n<body>\n  \n</body>\n</html>');
                                            }
                                        }}
                                        className="w-full text-left px-3 py-1 text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                                    >
                                        Clear All
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Validation Panel */}
                {showValidation && validation.errors.length > 0 && (
                    <div className={`p-3 border-b ${settings.theme === 'dark' ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-yellow-50'}`}>
                        <div className="flex items-center space-x-2 mb-2">
                            <AlertCircle className="w-4 h-4 text-yellow-500" />
                            <span className="text-sm font-medium">Code Issues ({validation.errors.length})</span>
                        </div>
                        <div className="space-y-1 max-h-20 overflow-y-auto">
                            {validation.errors.slice(0, 3).map((error, index) => (
                                <div key={index} className="text-xs flex items-center space-x-2">
                                    <span className={`px-1 rounded text-white ${error.type === 'error' ? 'bg-red-500' :
                                            error.type === 'warning' ? 'bg-yellow-500' : 'bg-blue-500'
                                        }`}>
                                        {error.type}
                                    </span>
                                    <span>Line {error.line}:</span>
                                    <span>{error.message}</span>
                                </div>
                            ))}
                            {validation.errors.length > 3 && (
                                <div className="text-xs text-gray-500">
                                    ...and {validation.errors.length - 3} more issues
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Main Content */}
                <div className="flex-1 flex overflow-hidden">
                    {/* Code Editor */}
                    {(activeTab === 'code' || activeTab === 'split') && (
                        <div className={`${activeTab === 'split' ? 'w-1/2' : 'w-full'} flex flex-col border-r ${settings.theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                            <div className={`p-2 text-sm font-medium ${settings.theme === 'dark' ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-600'} border-b flex items-center justify-between`}>
                                <div className="flex items-center space-x-2">
                                    <button
                                        onClick={() => setIsCollapsed(!isCollapsed)}
                                        className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
                                    >
                                        {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                    </button>
                                    <span>HTML Editor</span>
                                    {settings.autoSave && (
                                        <div className="flex items-center space-x-1 text-xs text-green-500">
                                            <CheckCircle className="w-3 h-3" />
                                            <span>Auto-saved</span>
                                        </div>
                                    )}
                                </div>
                                <div className="flex items-center space-x-4 text-xs">
                                    <span>{lineCount} lines</span>
                                    <span>•</span>
                                    <span>{charCount} chars</span>
                                    <span>•</span>
                                    <span>{wordCount} words</span>
                                </div>
                            </div>

                            {!isCollapsed && (
                                <div className="flex-1 relative overflow-auto">
                                    <div className="relative min-h-full">
                                        {settings.lineNumbers && (
                                            <div className={`absolute left-0 top-0 w-16 ${settings.theme === 'dark' ? 'bg-gray-800 text-gray-500 border-r border-gray-700' : 'bg-gray-50 text-gray-400 border-r border-gray-200'} text-xs font-mono select-none z-10`}>
                                                <div className="p-3 leading-relaxed" style={{ lineHeight: '1.6' }}>
                                                    {Array.from({ length: Math.max(lineCount, 30) }, (_, i) => (
                                                        <div key={i + 1} className="text-right pr-2 h-6 flex items-center justify-end">
                                                            {i + 1}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        <div className="relative w-full min-h-full">
                                            {/* Syntax highlighting overlay - BEHIND the textarea */}
                                            <div
                                                className={`absolute top-0 left-0 right-0 pointer-events-none font-mono leading-relaxed ${settings.lineNumbers ? 'pl-20' : 'pl-4'
                                                    } pr-4 py-3 overflow-hidden z-1`}
                                                style={{
                                                    fontSize: `${settings.fontSize}px`,
                                                    lineHeight: '1.6',
                                                    minHeight: `${Math.max(lineCount * 24, 400)}px`,
                                                    fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace',
                                                    whiteSpace: settings.wordWrap ? 'pre-wrap' : 'pre',
                                                    wordWrap: settings.wordWrap ? 'break-word' : 'normal',
                                                    tabSize: settings.tabSize
                                                }}
                                            >
                                                <style>{`
                        .tag { color: ${settings.theme === 'dark' ? '#f87171' : '#dc2626'}; font-weight: 600; }
                        .attr { color: ${settings.theme === 'dark' ? '#fbbf24' : '#d97706'}; font-weight: 500; }
                        .string { color: ${settings.theme === 'dark' ? '#34d399' : '#059669'}; }
                        .comment { color: ${settings.theme === 'dark' ? '#9ca3af' : '#6b7280'}; font-style: italic; }
                        .bracket { color: ${settings.theme === 'dark' ? '#60a5fa' : '#2563eb'}; font-weight: 600; }
                        .doctype { color: ${settings.theme === 'dark' ? '#a78bfa' : '#7c3aed'}; font-weight: 600; }
                        .entity { color: ${settings.theme === 'dark' ? '#fb7185' : '#e11d48'}; }
                      `}</style>
                                                <div dangerouslySetInnerHTML={{ __html: highlightedCode }} />
                                            </div>

                                            <textarea
                                                ref={textareaRef}
                                                value={htmlCode}
                                                onChange={(e) => setHtmlCode(e.target.value)}
                                                className={`w-full min-h-full resize-none font-mono leading-relaxed ${settings.lineNumbers ? 'pl-20' : 'pl-4'
                                                    } pr-4 py-3 bg-transparent border-none outline-none relative z-10 ${settings.theme === 'dark' ? 'text-transparent caret-white' : 'text-transparent caret-gray-900'
                                                    }`}
                                                style={{
                                                    fontSize: `${settings.fontSize}px`,
                                                    lineHeight: '1.6',
                                                    minHeight: `${Math.max(lineCount * 24, 400)}px`,
                                                    whiteSpace: settings.wordWrap ? 'pre-wrap' : 'pre',
                                                    wordWrap: settings.wordWrap ? 'break-word' : 'normal',
                                                    fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace',
                                                    tabSize: settings.tabSize
                                                }}
                                                spellCheck={false}
                                                autoComplete="off"
                                                autoCorrect="off"
                                                autoCapitalize="off"
                                                onKeyDown={(e) => {
                                                    if (e.ctrlKey && e.key === 'Enter') {
                                                        e.preventDefault();
                                                        runCode();
                                                    }
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Preview */}
                    {(activeTab === 'preview' || activeTab === 'split') && (
                        <div className={`${activeTab === 'split' ? 'w-1/2' : 'w-full'} flex flex-col`}>
                            <div className={`p-2 text-sm font-medium ${settings.theme === 'dark' ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-600'} border-b flex items-center justify-between`}>
                                <span>Live Preview</span>
                                <div className="flex items-center space-x-2">
                                    <span className={`text-xs px-2 py-1 rounded ${autoRun && settings.livePreview ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
                                        {autoRun && settings.livePreview ? 'Live' : 'Manual'}
                                    </span>
                                    <button
                                        onClick={runCode}
                                        className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                                        title="Force Refresh Preview"
                                    >
                                        <RotateCcw className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>

                            <div className="flex-1 bg-white flex items-center justify-center">
                                <div
                                    className="w-full h-full flex items-center justify-center"
                                    style={{
                                        maxWidth: showDevicePreview ? deviceDimensions[selectedDevice].width : '100%',
                                        maxHeight: showDevicePreview ? deviceDimensions[selectedDevice].height : '100%'
                                    }}
                                >
                                    <iframe
                                        ref={iframeRef}
                                        className={`border-none ${showDevicePreview && selectedDevice !== 'desktop' ? 'border border-gray-300 rounded-lg shadow-lg' : 'w-full h-full'}`}
                                        style={{
                                            width: showDevicePreview ? deviceDimensions[selectedDevice].width : '100%',
                                            height: showDevicePreview ? deviceDimensions[selectedDevice].height : '100%'
                                        }}
                                        title="HTML Preview"
                                        sandbox="allow-scripts allow-same-origin allow-forms allow-modals"
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Status Bar */}
                <div className={`flex items-center justify-between px-4 py-2 text-xs ${settings.theme === 'dark' ? 'bg-gray-800 text-gray-400 border-gray-700' : 'bg-gray-100 text-gray-600 border-gray-200'} border-t`}>
                    <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-1">
                            {validation.isValid ? (
                                <CheckCircle className="w-3 h-3 text-green-500" />
                            ) : (
                                <AlertCircle className="w-3 h-3 text-red-500" />
                            )}
                            <span>{validation.isValid ? 'Valid HTML' : `${validation.errors.length} issues`}</span>
                        </div>
                        <span>•</span>
                        <span>{charCount} characters</span>
                        <span>•</span>
                        <span>{lineCount} lines</span>
                        <span>•</span>
                        <span>{wordCount} words</span>
                        {settings.autoSave && (
                            <>
                                <span>•</span>
                                <div className="flex items-center space-x-1">
                                    <Cpu className="w-3 h-3 text-green-500" />
                                    <span>Auto-save enabled</span>
                                </div>
                            </>
                        )}
                    </div>

                    <div className="flex items-center space-x-4">
                        <button
                            onClick={() => setShowFeatureInfo(!showFeatureInfo)}
                            className="flex items-center space-x-1 hover:text-blue-500 transition-colors"
                            title="View Features"
                        >
                            <Info className="w-3 h-3" />
                            <span>Features</span>
                        </button>
                        <span>•</span>
                        <span>HTML5</span>
                        <span>•</span>
                        <span>{settings.theme === 'dark' ? 'Dark' : 'Light'} Theme</span>
                        <span>•</span>
                        <span>Font: {settings.fontSize}px</span>
                        {showDevicePreview && (
                            <>
                                <span>•</span>
                                <span className="capitalize">{selectedDevice} Preview</span>
                            </>
                        )}
                    </div>
                </div>

                {/* Feature Information Panel */}
                {showFeatureInfo && (
                    <div className={`${settings.theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-t max-h-96 overflow-y-auto`}>
                        <div className="p-4">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold flex items-center space-x-2">
                                    <Sparkles className="w-5 h-5 text-blue-500" />
                                    <span>Advanced HTML Viewer Features</span>
                                </h3>
                                <button
                                    onClick={() => setShowFeatureInfo(false)}
                                    className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                                >
                                    <Minimize2 className="w-4 h-4" />
                                </button>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {features.map((feature, index) => (
                                    <div key={index} className={`p-4 rounded-lg border ${settings.theme === 'dark' ? 'border-gray-700 bg-gray-900/50' : 'border-gray-200 bg-gray-50'} transition-all duration-200 hover:shadow-md`}>
                                        <div className="flex items-center space-x-2 mb-3">
                                            <div className="p-2 rounded-lg bg-blue-500/10 text-blue-500">
                                                {feature.icon}
                                            </div>
                                            <h4 className="font-semibold text-sm">{feature.category}</h4>
                                        </div>
                                        <ul className="space-y-2">
                                            {feature.items.map((item, itemIndex) => (
                                                <li key={itemIndex} className="text-xs flex items-start space-x-2">
                                                    <div className="w-1.5 h-1.5 rounded-full bg-green-500 mt-1.5 flex-shrink-0" />
                                                    <span className={settings.theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}>{item}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                ))}
                            </div>

                            <div className={`mt-6 p-4 rounded-lg ${settings.theme === 'dark' ? 'bg-blue-900/20 border border-blue-800' : 'bg-blue-50 border border-blue-200'}`}>
                                <div className="flex items-start space-x-3">
                                    <Star className="w-5 h-5 text-yellow-500 mt-0.5" />
                                    <div>
                                        <h4 className="font-semibold text-sm mb-2">Pro Tips for Best Experience</h4>
                                        <ul className="text-xs space-y-1">
                                            <li>• Use <kbd className="px-1 py-0.5 bg-gray-200 dark:bg-gray-700 rounded text-xs">Ctrl+Enter</kbd> to quickly run your code</li>
                                            <li>• Enable auto-save to never lose your work</li>
                                            <li>• Use device preview to test responsive designs</li>
                                            <li>• Check validation panel for HTML best practices</li>
                                            <li>• Explore different export formats for sharing</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-4 text-center">
                                <p className={`text-xs ${settings.theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                    ✨ Built with React, TypeScript, and modern web technologies for the best development experience
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Keyboard Shortcuts Help (Hidden by default, can be toggled) */}
                <div className="hidden">
                    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className={`max-w-md w-full mx-4 ${settings.theme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6`}>
                            <h3 className="text-lg font-semibold mb-4">Keyboard Shortcuts</h3>
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <span>Run Code</span>
                                    <kbd className="px-2 py-1 bg-gray-200 dark:bg-gray-700 rounded text-xs">Ctrl+Enter</kbd>
                                </div>
                                <div className="flex justify-between">
                                    <span>Format Code</span>
                                    <kbd className="px-2 py-1 bg-gray-200 dark:bg-gray-700 rounded text-xs">Ctrl+Shift+F</kbd>
                                </div>
                                <div className="flex justify-between">
                                    <span>Search</span>
                                    <kbd className="px-2 py-1 bg-gray-200 dark:bg-gray-700 rounded text-xs">Ctrl+F</kbd>
                                </div>
                                <div className="flex justify-between">
                                    <span>Toggle Theme</span>
                                    <kbd className="px-2 py-1 bg-gray-200 dark:bg-gray-700 rounded text-xs">Ctrl+Shift+T</kbd>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <HTMLViewerFeatures />
        </>
    );
};

export default AdvancedHTMLViewer;